import { Bound } from "lib/@components/binding/Bound"
import { List } from "@mui/material"
import { useMemo } from "react"
import { getScheduleEditorLegislation } from "./controller/get-schedule-editor-legislation"
import { IsEditMode, NotEditMode } from "lib/@components/edit-mode"
import { Sortable } from "lib/@components/Sortable"
import { useRefresh } from "lib/@hooks/useRefresh"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DraggableLegislationItem, LegislationItem } from "./legislation-item"
import { AddLegislationButton } from "./add-legislation-button"
import { getLegislationLinkHealth } from "./controller/get-legislation-link-health"
import { useParams } from "react-router"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { withComparison } from "lib/graphql/comparison-function"
import { isSame } from "library/normalized-stringify/index.mjs"

export function AuthoringGuidance() {
    const { target, editMode, onChange } = useBoundContext()
    const { projectId, scheduleId } = useParams()
    const draftId = createDraftId(projectId, scheduleId)
    const refresh = useRefresh(onChange)
    target.legislation ??= []
    const references = target.legislation
    const maxHeight = getListMaxHeight(editMode)

    const ids = useMemo(extractIds, [references])
    // Refetch on this draft's own changes (draftId) and on edits to legislation it references (legacyIds);
    // an edit to unrelated legislation refetches nothing.
    const legislation = getScheduleEditorLegislation.useResults(
        { legacyIds: ids, projectId },
        refreshOnlyFor([draftId, ...ids]),
        withInitialValue([])
    )
    const healthResults = getLegislationLinkHealth.useResults(
        { ids },
        withInitialValue([]),
        withComparison((left, right) => isSame(left, right, { invalidChars: [] }))
    )
    const legislationHealth = useMemo(buildHealthMap, [healthResults])

    return (
        <Bound refresh={refresh} legislation={legislation} legislationHealth={legislationHealth}>
            <IsEditMode>
                <Sortable items={references} onOrdered={orderItems}>
                    <List sx={{ maxHeight, overflow: "auto" }}>
                        <Repeat list={references} item={<DraggableLegislationItem />} />
                    </List>
                </Sortable>

                <AddLegislationButton />
            </IsEditMode>
            <NotEditMode>
                <List sx={{ maxHeight, overflow: "auto" }}>
                    <Repeat list={references} item={<LegislationItem />} />
                </List>
            </NotEditMode>
        </Bound>
    )

    function extractIds() {
        // Sort so the query identity is independent of display order — reordering references
        // in the UI must not re-fetch an identical set.
        return [...new Set(references.map(String))].sort()
    }

    function buildHealthMap() {
        if (!Array.isArray(healthResults)) return {}
        return Object.fromEntries(healthResults.filter(Boolean).map((r) => [String(r.id), r]))
    }

    function orderItems() {
        refresh()
    }
}

function getListMaxHeight(editMode) {
    if (!editMode) return undefined
    const max = 345
    return max
}
